import React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useParams, useHistory } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';

import { PlainButton } from 'ui/public/button';

import { ResultKind } from 'services/base/types';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { BandwidthBox } from './bandwidth_box/bandwidth_box';
import { DeviceBandwidth } from './device_bandwidth/device_bandwidth';

import { BandwidthPrioritizationStore } from './bandwidth_prioritization_store';

import Messages from './bandwidth_prioritization_messages';
import baseStyles from './bandwidth_prioritization.module.css';

type BandwidthPrioritizationStyles = {
  container: string;
  row: string;
  column: string;
  headerBackground: string;
  headerContent: string;
  header: string;
  headerText: string;
  save: string;
  body: string;
};

const themedStyles: ThemedStyles<BandwidthPrioritizationStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    row: baseStyles.row,
    column: baseStyles.column,
    headerBackground: baseStyles.daylightHeaderBackground,
    headerContent: baseStyles.baseHeaderContent,
    header: baseStyles.baseHeader,
    headerText: baseStyles.daylightHeaderText,
    save: baseStyles.daylightSave,
    body: baseStyles.daylightBody,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    row: baseStyles.row,
    column: baseStyles.column,
    headerBackground: baseStyles.midnightHeaderBackground,
    headerContent: baseStyles.baseHeaderContent,
    header: baseStyles.baseHeader,
    headerText: baseStyles.midnightHeaderText,
    save: baseStyles.midnightSave,
    body: baseStyles.midnightBody,
  },
};


type BaseBandwidthPrioritizationProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseBandwidthPrioritization = observer(({ pangolin, domain }: BaseBandwidthPrioritizationProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const hosts = domain.hosts;

  const [store, setStore] = React.useState(() => new BandwidthPrioritizationStore(hosts));
  React.useEffect(() => { setStore(new BandwidthPrioritizationStore(hosts)); }, [hosts]);

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, domain, history]);

  return (
    <form onSubmit={onSubmit} className={styles.container}>
      <div className={styles.headerBackground}>
        <div className={styles.headerContent}>
          <div className={styles.header}>
            <div className={styles.row}>
              <BackButton/>
              <div className={styles.headerText}>
                {Messages.editBandwidth()}
              </div>
              <PlainButton type='submit'
                busy={store.busy} reserveBusyIconArea={true}
                className={styles.save}>{Messages.save()}</PlainButton>
            </div>
          </div>
          <BandwidthBox pangolin={pangolin}/>
        </div>
      </div>
      <div className={styles.body}>
        {store.children.map(({ host, store: childStore }) => (
          <DeviceBandwidth host={host} store={childStore} key={host.id}/>
        ))}
      </div>
    </form>
  );
});

export const BandwidthPrioritization = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseBandwidthPrioritization
      pangolin={pangolin}
      domain={domain}
    />
  );
});
